<script lang="ts" setup>
import { computed } from 'vue';

import { RemoveHotelSelectionFiltersOrigin } from '@apps/main/new-desktop/core/models';

import type { AppMultiSelectOption } from '@libs/ui';
import { AppTag } from '@libs/ui';
import type { BoardBasisCode } from '@libs/trip-planner-interfaces';

import type { HotelFilterMealsUpdate } from '../../model/hotelFilters';

import { getMealTypeForFilter } from './hotelMealFilters.helper';

interface MealFiltersTags {
  name: string;
  id: BoardBasisCode;
}

interface HotelMealFiltersProps {
  options?: AppMultiSelectOption<BoardBasisCode>[];
}

const props = defineProps<HotelMealFiltersProps>();

interface HotelMealFiltersEmits {
  (
    eventName: 'meal-option-removed',
    context: {
      filter: HotelFilterMealsUpdate;
      source: RemoveHotelSelectionFiltersOrigin;
    }
  ): void;
}

const emit = defineEmits<HotelMealFiltersEmits>();

const tags = computed<MealFiltersTags[] | undefined>(() =>
  props.options
    ?.filter((option) => option.selected)
    .map((option) => ({ name: option.label, id: option.id }))
);

function onRemoveOption(id: BoardBasisCode): void {
  emit('meal-option-removed', {
    filter: {
      type: getMealTypeForFilter(id),
      value: false,
    },
    source: RemoveHotelSelectionFiltersOrigin.HotelDetails,
  });
}
</script>

<template>
  <section class="hotel-meal-filters-empty-state">
    <header class="title">
      {{ $t('planner.hotel_details_room_warning.title') }}
    </header>
    <main class="description">
      {{ $t('planner.hotel_details_room_warning.info') }}
    </main>
    <footer v-if="tags" class="tags">
      <AppTag
        v-for="tag in tags"
        :key="tag.id + tag.name"
        type="modern-filter"
        :icon="{
          name: 'closeInCircle',
          size: '18',
          position: 'right',
          color: 'color-neutral-70',
          hoverColor: 'color-soft-black',
          clickable: true,
          ariaLabel: $t('a11y.remove_filter'),
        }"
        :text-wrap="false"
        :clickable="false"
        @icon-click="onRemoveOption(tag.id)"
      >
        <template #title>{{ tag.name }}</template>
      </AppTag>
    </footer>
  </section>
</template>

<style lang="scss" scoped>
.hotel-meal-filters-empty-state {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: var(--spacing-12);
  margin: var(--spacing-24);
  padding: var(--spacing-32);
  background-color: var(--color-neutral-10);
  border-radius: var(--border-radius-12);

  @include for-less-or-equal-than-599-px {
    margin: var(--spacing-16);
  }

  .title {
    text-align: center;

    @include font-heading-5;

    @include for-less-or-equal-than-599-px {
      @include font-heading-6;
    }
  }

  .description {
    text-align: center;

    @include font-body-l-light;

    @include for-less-or-equal-than-599-px {
      @include font-body-s-light;
    }
  }

  .tags {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-8);
  }
}
</style>
